import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Eye, Clock, Search, X,
  ChevronLeft, ChevronRight, Check, XCircle,
  Download, Filter, CheckCircle2, AlertTriangle, Calendar, Loader2, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useAdminFreelancerProfiles, type ReviewAction, type CourseEnrollmentWithTitle } from '@/lib/hooks/use-admin-freelancer-profiles';
import type { FreelancerProfileWithStatus } from '@/lib/api/services';
import SkillsFilter from '@/components/admin/SkillsFilter';
import { cn } from '@/lib/utils';

// Course chip component for compact display
function CourseChip({ enrollment }: { enrollment: CourseEnrollmentWithTitle }) {
  const isCompleted = !!enrollment.completed_at;
  const progress = enrollment.progress_percent || 0;

  // Truncate course title
  const shortTitle = enrollment.course_title.length > 12
    ? enrollment.course_title.slice(0, 10) + '...'
    : enrollment.course_title;

  // Format date compactly
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap",
        isCompleted
          ? "bg-emerald-100 text-emerald-700"
          : progress > 0
            ? "bg-blue-100 text-blue-700"
            : "bg-slate-100 text-slate-600"
      )}
      title={`${enrollment.course_title}: ${progress}%${enrollment.completed_at ? ` - Completed ${enrollment.completed_at.split('T')[0]}` : ''}`}
    >
      {isCompleted ? (
        <CheckCircle2 className="w-2.5 h-2.5" />
      ) : (
        <span className="font-bold">{progress}%</span>
      )}
      <span>{shortTitle}</span>
      {enrollment.completed_at && (
        <span className="text-[9px] opacity-75">{formatDate(enrollment.completed_at)}</span>
      )}
    </span>
  );
}

const PAGE_SIZE = 10;

// Stat card component - displays count in "X/total" format for freelancer stats
function StatCard({
  icon: Icon,
  count,
  total,
  label,
  iconBgClass = 'bg-[rgba(70,105,237,0.10)]'
}: {
  icon: React.ElementType;
  count: number;
  total: number;
  label: string;
  iconBgClass?: string;
}) {
  return (
    <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center">
      <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mb-4", iconBgClass)}>
        <Icon className="w-6 h-6 text-[#4669ed]" />
      </div>
      <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
        {count}<span className="text-slate-400 text-sm">/{total}</span>
      </div>
      <div className="text-sm text-slate-500 mt-2">
        {label}
      </div>
    </div>
  );
}

// Status badge component matching the design
function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status || status === 'draft') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
        <Clock className="w-4 h-4" />
        Draft
      </span>
    );
  }

  switch (status) {
    case 'submitted':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(245,158,11,0.10)] text-amber-500">
          <Clock className="w-4 h-4" />
          Pending Review
        </span>
      );
    case 'changes_requested':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(245,158,11,0.10)] text-amber-500">
          <AlertTriangle className="w-4 h-4" />
          Changes Requested
        </span>
      );
    case 'approved':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(16,185,129,0.10)] text-emerald-500">
          <CheckCircle2 className="w-4 h-4" />
          Approved
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(239,68,68,0.10)] text-red-500">
          <XCircle className="w-4 h-4" />
          Rejected
        </span>
      );
    case 'disabled':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-red-500">
          <XCircle className="w-4 h-4" />
          Disabled
        </span>
      );
    default:
      return null;
  }
}

export default function FreelancerProfileManagement() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled' | 'featured'>('all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState<'all' | 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected'>('all');
  const [courseCompletionFilter, setCourseCompletionFilter] = useState<'all' | 'completed' | 'not_completed'>('all');
  const [courseFilterId, setCourseFilterId] = useState('');
  const [courseCompletionDateFrom, setCourseCompletionDateFrom] = useState('');
  const [courseCompletionDateTo, setCourseCompletionDateTo] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'created_at' | 'course_completion_date' | 'submitted_for_review_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Temporary filter states for course completion (before applying)
  const [courseCompletionPopoverOpen, setCourseCompletionPopoverOpen] = useState(false);
  const [tempCourseFilterId, setTempCourseFilterId] = useState('');
  const [tempCourseCompletionDateFrom, setTempCourseCompletionDateFrom] = useState('');
  const [tempCourseCompletionDateTo, setTempCourseCompletionDateTo] = useState('');

  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<ReviewAction | null>(null);
  const [reviewComments, setReviewComments] = useState('');
  const [pendingEnabled, setPendingEnabled] = useState<{ id: string; value: boolean } | null>(null);
  const [pendingFeatured, setPendingFeatured] = useState<{ id: string; value: boolean } | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const {
    profiles,
    totalCount,
    totalPages,
    totalStats,
    isLoading,
    error,
    isAdmin,
    courses,
    reviewProfile,
    isReviewing,
    toggleFreelancerEnabled,
    isTogglingEnabled,
    toggleFreelancerFeatured,
    isTogglingFeatured
  } = useAdminFreelancerProfiles(
    { page, pageSize: PAGE_SIZE },
    {
      statusFilter,
      reviewStatusFilter: reviewStatusFilter !== 'all' ? reviewStatusFilter : undefined,
      courseCompletionFilter: courseCompletionFilter !== 'all' ? courseCompletionFilter : undefined,
      courseFilterId: courseFilterId || undefined,
      courseCompletionDateFrom: courseCompletionDateFrom || undefined,
      courseCompletionDateTo: courseCompletionDateTo || undefined,
      nameSearch: nameSearch || undefined,
      selectedSkills: selectedSkills.size > 0 ? selectedSkills : undefined,
    },
    { sortBy, sortOrder }
  );


  // Extract all unique skills from all profiles
  const availableSkills = useMemo(() => {
    const skillsSet = new Set<string>();
    profiles.forEach(profile => {
      const skills = Array.isArray(profile.skills) ? profile.skills as string[] : [];
      skills.forEach(skill => skillsSet.add(skill));
    });
    return Array.from(skillsSet).sort();
  }, [profiles]);

  const hasActiveFilters = statusFilter !== 'all' || reviewStatusFilter !== 'all' || courseCompletionFilter !== 'all' || courseFilterId || courseCompletionDateFrom || courseCompletionDateTo || nameSearch || selectedSkills.size > 0;

  const activeFilterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];
    if (reviewStatusFilter !== 'all') {
      chips.push({
        label: `Status: ${reviewStatusFilter.replace('_', ' ')}`,
        onRemove: () => setReviewStatusFilter('all'),
      });
    }
    if (courseFilterId || courseCompletionDateFrom || courseCompletionDateTo) {
      const courseName = courseFilterId
        ? courses.find(c => c.id === courseFilterId)?.title || 'Course'
        : 'Any course';
      const fromLabel = courseCompletionDateFrom ? new Date(courseCompletionDateFrom).toLocaleDateString() : '';
      const toLabel = courseCompletionDateTo ? new Date(courseCompletionDateTo).toLocaleDateString() : '';
      const dateRange = (fromLabel || toLabel)
        ? `: ${fromLabel}${fromLabel && toLabel ? ' - ' : ''}${toLabel}`
        : '';
      chips.push({
        label: `${courseName}${dateRange}`,
        onRemove: () => {
          setCourseFilterId('');
          setCourseCompletionDateFrom('');
          setCourseCompletionDateTo('');
        },
      });
    }
    selectedSkills.forEach(skill => {
      chips.push({
        label: `Skill: ${skill}`,
        onRemove: () => {
          const newSkills = new Set(selectedSkills);
          newSkills.delete(skill);
          setSelectedSkills(newSkills);
        },
      });
    });
    return chips;
  }, [reviewStatusFilter, selectedSkills, courseFilterId, courseCompletionDateFrom, courseCompletionDateTo, courses]);

  const handleClearFilters = () => {
    setStatusFilter('all');
    setReviewStatusFilter('all');
    setCourseCompletionFilter('all');
    setCourseFilterId('');
    setCourseCompletionDateFrom('');
    setCourseCompletionDateTo('');
    setNameSearch('');
    setSelectedSkills(new Set());
    setPage(1);
  };

  const handleReviewAction = (profileId: string, action: ReviewAction) => {
    setOpenDropdownId(null); // Close dropdown
    setSelectedProfileId(profileId);
    setReviewAction(action);
    setReviewComments('');
    setReviewDialogOpen(true);
  };

  const handleToggleEnabled = (profileId: string, currentEnabled: boolean) => {
    setOpenDropdownId(null); // Close dropdown
    const next = !currentEnabled;
    setPendingEnabled({ id: profileId, value: next });
    toggleFreelancerEnabled?.(
      { profileId, enable: next },
      { onSettled: () => setPendingEnabled(null) }
    );
  };

  const handleToggleFeatured = (profileId: string, currentFeatured: boolean) => {
    setOpenDropdownId(null); // Close dropdown
    const next = !currentFeatured;
    setPendingFeatured({ id: profileId, value: next });
    toggleFreelancerFeatured?.(
      { profileId, feature: next },
      { onSettled: () => setPendingFeatured(null) }
    );
  };

  const handleSubmitReview = () => {
    if (!selectedProfileId || !reviewAction || !reviewProfile) return;

    reviewProfile({
      profileId: selectedProfileId,
      action: reviewAction,
      comments: reviewComments.trim() || undefined,
    });

    setReviewDialogOpen(false);
    setSelectedProfileId(null);
    setReviewAction(null);
    setReviewComments('');
  };

  // Format relative time
  const formatTimeAgo = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
        <p className="text-slate-500">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-[40px] border border-slate-200 p-12 text-center">
        <p className="text-red-600">Error loading profiles: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stat Cards - showing X/total format for complete visibility */}
      <div className="flex gap-6">
        <StatCard icon={Clock} count={totalStats.pending} total={totalStats.total} label="Pending Review" />
        <StatCard icon={CheckCircle2} count={totalStats.approved} total={totalStats.total} label="Approved" />
        <StatCard icon={AlertTriangle} count={totalStats.changesRequested} total={totalStats.total} label="Changes Requested" />
        <StatCard icon={XCircle} count={totalStats.rejected} total={totalStats.total} label="Rejected" />
      </div>

      {/* Search & Filter Section */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6 shadow-sm space-y-4">
        <div className="flex gap-4 items-center">
          {/* Search Input */}
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or skills..."
              value={nameSearch}
              onChange={(e) => {
                setNameSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10 rounded-[40px] border-slate-200 h-12"
            />
          </div>

          {/* Status Filter */}
          <Select
            value={reviewStatusFilter}
            onValueChange={(value: typeof reviewStatusFilter) => {
              setReviewStatusFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="flex-shrink-0 min-w-[140px] rounded-[40px] bg-slate-100 border-slate-200 h-12">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Pending</SelectItem>
              <SelectItem value="changes_requested">Changes Requested</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>

          {/* Skills Filter */}
          <SkillsFilter
            availableSkills={availableSkills}
            selectedSkills={selectedSkills}
            onSkillsChange={(skills) => {
              setSelectedSkills(skills);
              setPage(1);
            }}
            className="flex-shrink-0 rounded-[40px] bg-slate-100 border border-slate-200 h-9 px-4"
          />

          {/* Course Completion Filter */}
          <Select
            value={courseCompletionFilter}
            onValueChange={(value: typeof courseCompletionFilter) => {
              setCourseCompletionFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="flex-shrink-0 min-w-[140px] rounded-[40px] bg-slate-100 border-slate-200 h-12">
              <SelectValue placeholder="All Training" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Training</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="not_completed">Not Completed</SelectItem>
            </SelectContent>
          </Select>

          {/* Course Completion Date Range Filter */}
          <Popover open={courseCompletionPopoverOpen} onOpenChange={(open) => {
            setCourseCompletionPopoverOpen(open);
            // Sync temporary states with actual states when opening
            if (open) {
              setTempCourseFilterId(courseFilterId);
              setTempCourseCompletionDateFrom(courseCompletionDateFrom);
              setTempCourseCompletionDateTo(courseCompletionDateTo);
            }
          }}>
            <PopoverTrigger asChild>
              <button className={cn(
                "flex-shrink-0 inline-flex items-center gap-2 px-4 rounded-[40px] bg-slate-100 border border-slate-200 h-9 text-sm font-medium whitespace-nowrap",
                (courseFilterId || courseCompletionDateFrom || courseCompletionDateTo) ? "text-[#4669ed]" : "text-slate-600"
              )}>
                <Calendar className="w-4 h-4" />
                <span>Course Completion</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4" align="end">
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-slate-900">Filter by Course Completion</h4>

                {/* Course Selector */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Course</Label>
                  <Select
                    value={tempCourseFilterId || '_any'}
                    onValueChange={(value) => {
                      setTempCourseFilterId(value === '_any' ? '' : value);
                    }}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Select a course" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any">Any course</SelectItem>
                      {courses.map((course: { id: string; title: string }) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">Completion Date Range</Label>
                  <div className="grid gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 w-12">From:</Label>
                      <Input
                        type="date"
                        value={tempCourseCompletionDateFrom}
                        onChange={(e) => {
                          setTempCourseCompletionDateFrom(e.target.value);
                        }}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 w-12">To:</Label>
                      <Input
                        type="date"
                        value={tempCourseCompletionDateTo}
                        onChange={(e) => {
                          setTempCourseCompletionDateTo(e.target.value);
                        }}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200">
                  {(tempCourseFilterId || tempCourseCompletionDateFrom || tempCourseCompletionDateTo) && (
                    <button
                      onClick={() => {
                        setTempCourseFilterId('');
                        setTempCourseCompletionDateFrom('');
                        setTempCourseCompletionDateTo('');
                      }}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Clear
                    </button>
                  )}
                  <div className="flex gap-2 ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setCourseCompletionPopoverOpen(false);
                      }}
                      className="h-8 text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setCourseFilterId(tempCourseFilterId);
                        setCourseCompletionDateFrom(tempCourseCompletionDateFrom);
                        setCourseCompletionDateTo(tempCourseCompletionDateTo);
                        setPage(1);
                        setCourseCompletionPopoverOpen(false);
                      }}
                      className="h-8 text-xs bg-[#4669ed] hover:bg-[#3654c9]"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Sort */}
          <Select
            value={`${sortBy}_${sortOrder}`}
            onValueChange={(value) => {
              const parts = value.split('_');
              if (parts.length >= 2) {
                const by = parts.slice(0, -1).join('_') as typeof sortBy;
                const order = parts[parts.length - 1] as typeof sortOrder;
                if (by && order && (order === 'asc' || order === 'desc')) {
                  setSortBy(by);
                  setSortOrder(order);
                  setPage(1);
                }
              }
            }}
          >
            <SelectTrigger className="flex-shrink-0 min-w-[160px] rounded-[40px] bg-slate-100 border-slate-200 h-12">
              <SelectValue placeholder="Sort: Newest" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at_desc">Sort: Newest</SelectItem>
              <SelectItem value="created_at_asc">Sort: Oldest</SelectItem>
              <SelectItem value="submitted_for_review_at_desc">Submitted (Latest)</SelectItem>
              <SelectItem value="submitted_for_review_at_asc">Submitted (Earliest)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Active Filter Chips */}
        {activeFilterChips.length > 0 && (
          <div className="flex gap-2 flex-wrap items-center">
            {activeFilterChips.map((chip, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-2 px-3 h-8 rounded-full text-xs font-semibold bg-[rgba(70,105,237,0.10)] text-[#4669ed]"
              >
                {chip.label}
                <button onClick={chip.onRemove} className="hover:text-[#3654c9]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
        {/* Table Header */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            All Geniuses ({totalCount})
          </h3>
          <div className="flex gap-2">
            <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[40px] border-2 border-slate-200 text-[#4669ed] font-bold text-sm hover:bg-slate-50">
              <Download className="w-5 h-5" />
              Export CSV
            </button>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[40px] bg-[#4669ed] text-white font-bold text-sm hover:bg-[#3654c9]"
              >
                <Filter className="w-5 h-5" />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-12 text-center text-slate-500">Loading profiles...</div>
        ) : profiles.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            No Genius profiles found matching your filters.
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <div className="grid grid-cols-[2.2fr_1fr_1.2fr_1.5fr_0.8fr_1.3fr] px-6 py-4 gap-4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Genius</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Status</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Skills</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Courses</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Applied</div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</div>
              </div>
            </div>

            {/* Table Rows */}
            <div className="divide-y divide-slate-100">
              {profiles.map((profile: FreelancerProfileWithStatus) => {
                const initials = profile.display_name
                  ?.split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) || '?';
                const skills = Array.isArray(profile.skills) ? profile.skills as string[] : [];
                const courseEnrollments = (profile as any).course_enrollments || [];
                const effectiveEnabled = pendingEnabled?.id === profile.id ? pendingEnabled!.value : profile.is_enabled;
                const effectiveFeatured = pendingFeatured?.id === profile.id ? pendingFeatured!.value : profile.is_featured;
                const isRowReviewing = isReviewing && selectedProfileId === profile.id;
                const isRowTogglingEnabled = isTogglingEnabled && pendingEnabled?.id === profile.id;
                const isRowTogglingFeatured = isTogglingFeatured && pendingFeatured?.id === profile.id;
                const isRowActionPending = isRowReviewing || isRowTogglingEnabled || isRowTogglingFeatured;

                return (
                  <div
                    key={profile.id}
                    onClick={() => navigate(`/genius/${profile.id}`)}
                  className="grid grid-cols-[2.2fr_1fr_1.2fr_1.5fr_0.8fr_1.3fr] px-6 py-5 gap-4 items-center hover:bg-slate-50/50 cursor-pointer transition-colors duration-150"
                  >
                    {/* Freelancer */}
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="w-10 h-10 bg-[#4669ed] text-white flex-shrink-0">
                        <AvatarImage src={profile.profile_picture_url || undefined} />
                        <AvatarFallback className="bg-[#4669ed] text-white font-bold text-sm">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-slate-900 truncate">{profile.display_name}</div>
                        <div className="text-xs text-slate-500 truncate">{profile.email}</div>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      <StatusBadge status={effectiveEnabled ? profile.review_status : 'disabled'} />
                    </div>

                    {/* Skills */}
                    <div className="flex gap-1.5 flex-wrap items-center">
                      {skills.length === 0 ? (
                        <span className="text-xs text-slate-400 italic">No skills</span>
                      ) : (
                        <>
                          {skills.slice(0, 2).map((skill) => (
                            <span
                              key={skill}
                              className="px-2.5 py-1 rounded-md bg-slate-100 text-xs font-semibold text-slate-700 whitespace-nowrap"
                            >
                              {skill}
                            </span>
                          ))}
                          {skills.length > 2 && (
                            <span className="px-2.5 py-1 rounded-md bg-slate-100 text-xs font-semibold text-slate-700">
                              +{skills.length - 2}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Courses */}
                    <div className="flex gap-1.5 flex-wrap items-center" onClick={(e) => e.stopPropagation()}>
                      {courseEnrollments.length === 0 ? (
                        <span className="text-xs text-slate-400 italic">No courses</span>
                      ) : (
                        <>
                          {courseEnrollments.slice(0, 2).map((enrollment: CourseEnrollmentWithTitle) => (
                            <CourseChip key={enrollment.course_id} enrollment={enrollment} />
                          ))}
                          {courseEnrollments.length > 2 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                              +{courseEnrollments.length - 2}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Applied */}
                    <div className="text-sm text-slate-900 font-medium">
                      {formatTimeAgo(profile.created_at)}
                    </div>

                    {/* Actions */}
                    <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
                      <DropdownMenu 
                        open={openDropdownId === profile.id} 
                        onOpenChange={(open) => setOpenDropdownId(open ? profile.id : null)}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="h-9 px-3 rounded-full text-xs flex items-center gap-2">
                            {isRowActionPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <MoreVertical className="w-4 h-4" />
                            )}
                            {isRowActionPending ? 'Processing...' : 'Actions'}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onSelect={() => {
                              navigate(`/genius/${profile.id}`);
                            }}
                            className="cursor-pointer"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View profile
                          </DropdownMenuItem>
                          {profile.review_status !== 'approved' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={isRowReviewing}
                                onSelect={() => {
                                  handleReviewAction(profile.id, 'approve');
                                }}
                                className="cursor-pointer"
                              >
                                {isRowReviewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                                {isRowReviewing ? 'Processing...' : 'Approve'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={isRowReviewing}
                                onSelect={() => {
                                  handleReviewAction(profile.id, 'request_changes');
                                }}
                                className="cursor-pointer"
                              >
                                {isRowReviewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                                {isRowReviewing ? 'Processing...' : 'Request changes'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={isRowReviewing}
                                onSelect={() => {
                                  handleReviewAction(profile.id, 'reject');
                                }}
                                className="cursor-pointer"
                              >
                                {isRowReviewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                                {isRowReviewing ? 'Processing...' : 'Reject'}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            disabled={isRowTogglingEnabled}
                            onSelect={() => {
                              handleToggleEnabled(profile.id, effectiveEnabled);
                            }}
                            className="cursor-pointer"
                          >
                            {isRowTogglingEnabled ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Switch checked={effectiveEnabled} className="scale-75 mr-2" />}
                            {effectiveEnabled ? 'Disable profile' : 'Enable profile'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={isRowTogglingFeatured}
                            onSelect={() => {
                              handleToggleFeatured(profile.id, effectiveFeatured);
                            }}
                            className="cursor-pointer"
                          >
                            {isRowTogglingFeatured ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Switch checked={effectiveFeatured} className="scale-75 mr-2" />}
                            {effectiveFeatured ? 'Unfeature profile' : 'Feature profile'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-6 border-t border-slate-200">
          <div className="text-sm text-slate-500">
            Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} Geniuses
          </div>
          {totalPages > 1 && (
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={cn(
                      "w-8 h-8 rounded-md border flex items-center justify-center text-sm font-bold",
                      page === pageNum
                        ? "bg-[#4669ed] border-[#4669ed] text-white"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-8 h-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' && 'Approve Profile'}
              {reviewAction === 'reject' && 'Reject Profile'}
              {reviewAction === 'request_changes' && 'Request Changes'}
            </DialogTitle>
            <DialogDescription>
              {reviewAction === 'approve' && 'Approve this profile and make it visible to clients.'}
              {reviewAction === 'reject' && 'Reject this profile. The Genius will need to create a new profile.'}
              {reviewAction === 'request_changes' && 'Request changes from the Genius. They can update and resubmit.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="comments">
                Comments {reviewAction === 'request_changes' && '(Required)'}
              </Label>
              <Textarea
                id="comments"
                placeholder={
                  reviewAction === 'approve'
                    ? 'Optional: Add any notes about this approval...'
                    : reviewAction === 'reject'
                    ? 'Explain why this profile is being rejected...'
                    : 'Describe what changes are needed...'
                }
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                rows={4}
                className="rounded-[16px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={isReviewing || (reviewAction === 'request_changes' && !reviewComments.trim())}
              className={cn(
                "rounded-full",
                reviewAction === 'approve' && 'bg-emerald-500 hover:bg-emerald-600',
                reviewAction === 'reject' && 'bg-red-500 hover:bg-red-600',
                reviewAction === 'request_changes' && 'bg-amber-500 hover:bg-amber-600'
              )}
            >
              {isReviewing ? 'Processing...' :
                reviewAction === 'approve' ? 'Approve' :
                reviewAction === 'reject' ? 'Reject' :
                'Request Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
